import {Bot, Maximize2, MessageSquarePlus, Minimize2, Phone, X} from 'lucide-react'
import React from 'react'
import {useIntl} from 'react-intl'

type ChatbotHeaderProps = {
  theme: 'light' | 'dark'
  brandMessageId?: string
  subtitleMessageId?: string
  showBetaBadge?: boolean
  showStatus?: boolean
  allowVoice?: boolean
  voiceCallDisabled?: boolean
  onStartCall?: () => void
  showFullscreenToggle?: boolean
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
  onClose?: () => void
  onNewChat?: () => void
  /** Replaces the default bot glyph in the avatar circle (e.g. the assistant mascot image). */
  avatar?: React.ReactNode
}

const ChatbotHeader: React.FC<ChatbotHeaderProps> = ({
  theme,
  avatar,
  brandMessageId = 'CHATBOT.WIDGET_BRAND',
  subtitleMessageId = 'CHATBOT.WIDGET_SUBTITLE_DEFAULT',
  showBetaBadge = false,
  showStatus = true,
  allowVoice = true,
  voiceCallDisabled = false,
  onStartCall,
  showFullscreenToggle = true,
  isFullscreen = false,
  onToggleFullscreen,
  onClose,
  onNewChat,
}) => {
  const intl = useIntl()
  const iconColor = theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.55)'

  return (
    <div className='chatbot-widget-header'>
      <div className='chatbot-widget-header__identity'>
        <div
          className={`chatbot-widget-header__avatar${
            avatar ? ' chatbot-widget-header__avatar--image' : ''
          }`}
          aria-hidden='true'
        >
          {avatar ?? <Bot size={22} strokeWidth={2.2} />}
        </div>
        <div className='chatbot-widget-header__titles'>
          <h5 className='chatbot-widget-header__brand'>
            <span className='chatbot-widget-header__brand-text'>
              {intl.formatMessage({id: brandMessageId})}
            </span>
            {showBetaBadge ? (
              <span className='chatbot-widget-header__beta'>
                {intl.formatMessage({id: 'CHATBOT.BETA_BADGE'})}
              </span>
            ) : null}
          </h5>
          <p className='chatbot-widget-header__subtitle'>
            {intl.formatMessage({id: subtitleMessageId})}
          </p>
          {showStatus ? (
            <div className='chatbot-widget-header__status'>
              <span className='chatbot-widget-header__status-dot' />
              {intl.formatMessage({id: 'CHATBOT.WIDGET_ONLINE'})}
            </div>
          ) : null}
        </div>
      </div>

      <div className='chatbot-widget-header__actions'>
        {onNewChat ? (
          <button
            type='button'
            onClick={onNewChat}
            className='chatbot-widget-header__btn'
            aria-label={intl.formatMessage({id: 'CHATBOT.NEW_CHAT'})}
            title={intl.formatMessage({id: 'CHATBOT.NEW_CHAT'})}
          >
            <MessageSquarePlus size={16} color={iconColor} />
          </button>
        ) : null}
        {allowVoice && onStartCall ? (
          <button
            type='button'
            onClick={onStartCall}
            disabled={voiceCallDisabled}
            className='chatbot-widget-header__btn'
            aria-label={intl.formatMessage({id: 'CHAT.VOICE.START_CALL'})}
            title={intl.formatMessage({id: 'CHAT.VOICE.START_CALL'})}
          >
            <Phone size={16} color={iconColor} />
          </button>
        ) : null}
        {showFullscreenToggle && onToggleFullscreen ? (
          <button
            type='button'
            onClick={onToggleFullscreen}
            className='chatbot-widget-header__btn'
            aria-label={
              isFullscreen
                ? intl.formatMessage({id: 'CHATBOT.EXIT_FULLSCREEN'})
                : intl.formatMessage({id: 'CHATBOT.ENTER_FULLSCREEN'})
            }
            title={
              isFullscreen
                ? intl.formatMessage({id: 'CHATBOT.EXIT_FULLSCREEN'})
                : intl.formatMessage({id: 'CHATBOT.ENTER_FULLSCREEN'})
            }
          >
            {isFullscreen ? (
              <Minimize2 size={16} color={iconColor} />
            ) : (
              <Maximize2 size={16} color={iconColor} />
            )}
          </button>
        ) : null}
        {onClose ? (
          <button
            type='button'
            onClick={onClose}
            className='chatbot-widget-header__btn'
            aria-label={intl.formatMessage({id: 'CHATBOT.CLOSE'})}
            title={intl.formatMessage({id: 'CHATBOT.CLOSE'})}
          >
            <X size={16} color={iconColor} />
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default ChatbotHeader
